/**
 * What a reply looked up, between its reasoning and its answer: one row per
 * lookup, drawn like the reasoning line.
 *
 * Running: the icon pulses and the row says what it is searching for. Back:
 * "Searched the web for …" and how many sources, which opens the list of
 * them — each a title and its site, opening in a new tab. No favicons: the
 * app's CSP loads nothing from other origins. A lookup that failed or found
 * nothing says so on its row, with nothing to open.
 */

import { useId, useState } from 'react';
import type { Lookup, Source } from '../api';
import { hostOf, lookupLead, lookupQuery, lookupResult, sourceHref } from './lookupsText';
import { Ph } from './ui';

const row = { display: 'flex', alignItems: 'center', gap: 8, height: 28, minWidth: 0 } as const;

const oneLine = { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' } as const;

function SourceRow({ source }: { source: Source }) {
  const host = hostOf(source.url);
  const href = sourceHref(source.url);
  const body = (
    <>
      <span style={{ ...oneLine, flex: '0 1 auto', minWidth: 0 }}>
        {source.title || host}
      </span>
      <span style={{ ...oneLine, flex: 'none', maxWidth: '50%', fontSize: 12, color: 'var(--text-faint)' }}>
        {host}
      </span>
    </>
  );
  const style = {
    ...row,
    padding: '0 10px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    color: 'var(--text-muted)'
  } as const;

  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="hov-soft" style={style}>
      {body}
    </a>
  ) : (
    <div style={style}>{body}</div>
  );
}

function LookupRow({ lookup, running }: { lookup: Lookup; running: boolean }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const result = lookupResult(lookup, running);
  const expandable = !running && lookup.sources.length > 0;

  const summary = (
    <>
      <Ph
        name={lookup.kind === 'web' ? 'globe' : 'book-open'}
        size={15}
        color={running ? 'var(--primary)' : 'var(--text-faint)'}
        className={running ? 'pulse' : undefined}
        style={{ flex: 'none' }}
      />
      {/* The query gives way first, so the lead, the closing quote and the
          count stay whole. */}
      <span style={{ display: 'flex', flex: '0 1 auto', minWidth: 0 }}>
        <span style={{ flex: 'none', whiteSpace: 'nowrap' }}>{`${lookupLead(lookup, running)} "`}</span>
        <span style={{ ...oneLine, flex: '0 1 auto', minWidth: 0 }}>{lookupQuery(lookup.query)}</span>
        <span style={{ flex: 'none' }}>"</span>
      </span>
      {result ? <span style={{ flex: 'none', color: 'var(--text-faint)' }}>{result}</span> : null}
    </>
  );

  if (!expandable) {
    return (
      <div
        role={running ? 'status' : undefined}
        style={{
          ...row,
          fontSize: running ? 13.5 : 13,
          color: running ? 'var(--text-muted)' : 'var(--text-faint)'
        }}
      >
        {summary}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div style={{ display: 'flex', minWidth: 0 }}>
        <button
          type="button"
          className="bare hov-soft"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen(!open)}
          style={{
            ...row,
            flex: '0 1 auto',
            maxWidth: 'calc(100% + 8px)',
            padding: '0 8px',
            marginLeft: -8,
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            color: 'var(--text-faint)'
          }}
        >
          {summary}
          <Ph name={open ? 'caret-up' : 'caret-down'} size={12} style={{ flex: 'none' }} />
        </button>
      </div>

      {open ? (
        <div
          id={panelId}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            paddingLeft: 10,
            borderLeft: '2px solid var(--border-subtle)'
          }}
        >
          {lookup.sources.map((s, i) => (
            <SourceRow key={i} source={s} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Lookups({
  lookups,
  running = []
}: {
  lookups: Lookup[];
  /** Indices into `lookups` still out; a stored reply has none. */
  running?: readonly number[];
}) {
  if (!lookups.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {lookups.map((l, i) => (
        <LookupRow key={i} lookup={l} running={running.includes(i)} />
      ))}
    </div>
  );
}
